// @ai-generated 2026-07-28T15:01:24+00:00 by niklas.eicker@tngtech.com using Claude Opus 5
import { AddressLines } from '@Components/AddressLines';
import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useCurrentPartnership, useSession } from '@Contexts/session';
import { useRequisitionOrderSnapshots, useRequisitionPrintOptions } from '@Hooks/Orders';
import { usePortalServiceLabels } from '@Hooks/Stammdaten/usePortalLabels';
import { useRemoteCacheInvalidation } from '@Hooks/useRemoteCacheInvalidation';
import {
    type ApiRequisition,
    type EnumUnit,
    getEnumUnitLabel,
    getPortalRequisitionOriginLabel,
    getPortalRequisitionStatusLabel,
    getPortalRequisitionStatusVariant,
    isSelfSubmittedRequisition,
} from '@Model';
import {
    Badge,
    Button,
    EdgeSpacer,
    FlexColumn,
    FloatingCard,
    Grid,
    GridColumn,
    type IFrameBodyProps,
    InlineBanner,
    ListRow,
    renderDateTime,
    renderDecimal,
    ScrollContainer,
    StatusTimeline,
    useTranslate,
    ValueWithBadges,
} from '@synqo/frontend-component-library';
import { IconChevronLeft, IconFileInvoice, IconPhone } from '@tabler/icons-react';
import { getRelativeDayLabel } from '@utils/relativeDayLabel';
import { formatRequisitionDesiredDate } from '@utils/requisitionDisplay';
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import { useClientNavigate } from '../../../Routing/useClientNavigate';
import {
    buildPortalRequisitionTimeline,
    collectPortalRequisitionInvoices,
    derivePortalRequisitionOrderFacts,
    type PortalRequisitionInvoice,
    PortalRequisitionContainersCard,
    PortalRequisitionDocumentsCard,
} from '@Modules/Portal';
import classes from './RequisitionDetail.module.css';

const FALLBACK_OPERATOR_NAME = 'Ihr Entsorger';

/**
 * The customer's view of one order. Read-only by design: a submitted order is changed by talking
 * to the operator, so there is no edit mode and no header action.
 */
export function RequisitionDetail(props: IFrameBodyProps<ApiRequisition>) {
    const i18n = useTranslate('');
    const navigate = useClientNavigate();
    const hasPermission = useHasPermission();
    const {
        current: { subpath },
    } = useSession();
    const requisition = props.data;
    const operatorName = useCurrentPartnership()?.mandant_display ?? i18n(FALLBACK_OPERATOR_NAME);

    useRemoteCacheInvalidation('requisitions', props.refetchData);

    const canSeeDocuments = hasPermission(Permission.DocumentsDocumentRead);
    const { printOptions, isLoading: printOptionsLoading } = useRequisitionPrintOptions(requisition.id);

    const { snapshots } = useRequisitionOrderSnapshots(requisition.id, !props.embedded);
    const orderFacts = useMemo(() => derivePortalRequisitionOrderFacts(snapshots), [snapshots]);

    const positions = useMemo(() => {
        const placed = (snapshots ?? []).flatMap((snapshot) =>
            [...snapshot.positions]
                .sort((one, other) => one.number - other.number)
                .map((position) => ({
                    id: position.id,
                    service: {
                        foreign_key: position.service_id,
                        display_string: position.label,
                        api_resource_name: 'services',
                    },
                    quantity: position.quantity,
                    unit: position.unit,
                })),
        );
        return placed.length > 0 ? placed : requisition.positions;
    }, [snapshots, requisition.positions]);

    const { ensure: ensureMaterialNames, labelOf: materialOf } = usePortalServiceLabels();
    useEffect(() => {
        ensureMaterialNames(positions.map((position) => position.service));
    }, [positions, ensureMaterialNames]);

    // Without the permission the invoices stay out of the note entirely, rather than becoming links
    // into a page the customer may not open.
    const canSeeInvoices = hasPermission(Permission.BillingInvoiceSnapshotsRead);
    const invoices = useMemo(
        () => (canSeeInvoices ? collectPortalRequisitionInvoices(snapshots) : []),
        [canSeeInvoices, snapshots],
    );

    const timelineSteps = useMemo(
        () =>
            buildPortalRequisitionTimeline({
                status: requisition.process_state,
                submittedAt: requisition.created_at,
                statusChangedAt: requisition.modified_at,
                operatorName,
                origin: requisition.origin,
                orderFacts,
                completedNote: renderCompletedNote(invoices, subpath, i18n),
            }),
        [
            requisition.process_state,
            requisition.created_at,
            requisition.modified_at,
            requisition.origin,
            operatorName,
            orderFacts,
            invoices,
            subpath,
            i18n,
        ],
    );

    // Same wording as the list, so a named "spätestens bis" does not go missing on the detail page.
    const desiredDate = formatRequisitionDesiredDate(requisition);
    const relativeDate = getRelativeDayLabel(requisition.date_from);

    const isRecordedForCustomer = !isSelfSubmittedRequisition(requisition.origin);
    const originLabel = getPortalRequisitionOriginLabel(requisition.origin);

    const goToList = useCallback(() => navigate(`/requisitions`), [navigate]);
    const onBack = props.embedded ? props.events.onBack : goToList;

    return (
        <ScrollContainer width="100%" height="100%">
            <div className={classes.content}>
                {onBack && (
                    <Button appearance="transparent" onClick={onBack} label={i18n('Alle Bestellungen')}>
                        <IconChevronLeft size={16} />
                        &nbsp;{i18n('Alle Bestellungen')}
                    </Button>
                )}

                <div className={classes.header}>
                    <ValueWithBadges value={<h1 className={classes.title}>{requisition.number}</h1>}>
                        <Badge withDot variant={getPortalRequisitionStatusVariant(requisition.process_state)}>
                            {getPortalRequisitionStatusLabel(requisition.process_state)}
                        </Badge>
                        {originLabel && <Badge variant="accent">{i18n(originLabel)}</Badge>}
                    </ValueWithBadges>
                    <p className={classes.subtitle}>
                        {[
                            requisition.created_at
                                ? `${i18n('bestellt am')} ${renderDateTime(requisition.created_at, 'date')}`
                                : undefined,
                            requisition.site?.address?.display_string,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                </div>

                {isRecordedForCustomer && (
                    <InlineBanner
                        icon={<IconPhone size={18} />}
                        title={`${operatorName} ${i18n('hat diese Bestellung für Sie erfasst.')}`}
                        description={i18n(
                            'Änderungen und Stornierungen nehmen Sie bitte direkt mit Ihrem Entsorger auf.',
                        )}
                    />
                )}

                <Grid columns={12}>
                    <GridColumn span={7}>
                        <FlexColumn gap="md" align="stretch">
                            <FloatingCard title={i18n('Was passiert mit meiner Bestellung?')} collapsible={false}>
                                <StatusTimeline steps={timelineSteps} label={i18n('Bestellstatus')} />
                            </FloatingCard>

                            <PortalRequisitionContainersCard snapshots={snapshots} />
                        </FlexColumn>
                    </GridColumn>
                    <GridColumn span={5}>
                        <FlexColumn gap="md" align="stretch">
                            {canSeeDocuments && (
                                <PortalRequisitionDocumentsCard
                                    printOptions={printOptions}
                                    isLoading={printOptionsLoading}
                                />
                            )}

                            <FloatingCard title={i18n('Bestelldetails')} collapsible={false}>
                                <Detail
                                    label={i18n('Leistungsort')}
                                    value={<AddressLines location={requisition.site} />}
                                />
                                <Detail
                                    label={i18n('Bestellt am')}
                                    value={
                                        requisition.created_at ? renderDateTime(requisition.created_at, 'date') : '—'
                                    }
                                />
                                <Detail
                                    label={i18n('Wunschtermin')}
                                    value={
                                        <>
                                            {i18n(desiredDate)}
                                            {relativeDate && (
                                                <Badge variant="primary" size="xxs">
                                                    {relativeDate}
                                                </Badge>
                                            )}
                                        </>
                                    }
                                />
                                {requisition.remarks && (
                                    <Detail label={i18n('Bemerkung')} value={requisition.remarks} />
                                )}
                            </FloatingCard>

                            {positions.length > 0 && (
                                <FloatingCard title={i18n('Positionen')} collapsible={false}>
                                    {positions.map((position) => (
                                        <ListRow
                                            key={position.id}
                                            className={classes.position}
                                            title={materialOf(position.service) || '—'}
                                            rightSection={formatQuantity(position.quantity, position.unit)}
                                        />
                                    ))}
                                </FloatingCard>
                            )}
                        </FlexColumn>
                    </GridColumn>
                </Grid>
            </div>
            {!props.embedded && <EdgeSpacer />}
        </ScrollContainer>
    );
}

/**
 * The note under the completed step: when the order was finished and which invoices bill it. Each
 * invoice is named and linked on its own, because the customer's question is "where is my invoice",
 * not "where are all my invoices" — an order billed in parts simply gets one link per invoice.
 * @ai-generated 2026-08-28T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
function renderCompletedNote(
    invoices: readonly PortalRequisitionInvoice[],
    subpath: string,
    i18n: ReturnType<typeof useTranslate>,
): ReactNode {
    const doneOn = i18n('Diese Bestellung ist abgeschlossen.');

    if (invoices.length === 0) {
        return doneOn;
    }

    return (
        <>
            {doneOn} {i18n(invoices.length === 1 ? 'Ihre Rechnung dazu:' : 'Ihre Rechnungen dazu:')}
            <div className={classes.invoiceLinks}>
                {invoices.map((invoice) => (
                    <InvoiceLink key={invoice.id} invoice={invoice} subpath={subpath} />
                ))}
            </div>
        </>
    );
}

/**
 * One invoice of the completed note, as a chip: named after the invoice number the customer sees on
 * the document itself, and sized so that several of them wrap instead of running into a wall of text.
 * @ai-generated 2026-08-28T00:00:00+00:00 by matthias.gessinger@synqony.com using Claude Opus 5 (1M context)
 */
function InvoiceLink({ invoice, subpath }: { invoice: PortalRequisitionInvoice; subpath: string }) {
    const i18n = useTranslate('');
    // A snapshot without a number is not worth hiding — it is still the invoice for this order.
    const name = invoice.number || i18n('Rechnung');
    const label = invoice.number ? `${i18n('Rechnung')} ${invoice.number}` : name;

    return (
        <Link
            to={`/${subpath}/invoices/${invoice.id}`}
            className={classes.invoiceLink}
            aria-label={`${label} ${i18n('öffnen')}`}
        >
            <IconFileInvoice size={14} aria-hidden />
            {name}
        </Link>
    );
}

function formatQuantity(quantity: number, unit: EnumUnit): string {
    return `${renderDecimal(quantity)} ${getEnumUnitLabel(unit)}`;
}

function Detail({ label, value }: { label: string; value: ReactNode }) {
    return (
        <div className={classes.detail}>
            <span className={classes.detailLabel}>{label}</span>
            <span className={classes.detailValue}>{value}</span>
        </div>
    );
}
